import { toast } from "@devhop/ui";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { formatCfApiErrorMessage } from "@/shared/lib/cf-api-error-messages";
import { liveApi } from "./client";

export const CATALOG_PAGE_LIMIT = 20;

const liveKeys = {
  all: ["live"] as const,
  list: (params?: { pageId?: string; q?: string; status?: string }) =>
    [...liveKeys.all, "list", params] as const,
  draft: (id: string) => [...liveKeys.all, "draft", id] as const,
  draftCatalog: (
    id: string,
    params?: {
      q?: string;
      offset?: number;
      limit?: number;
      groupByProduct?: boolean;
    },
  ) => [...liveKeys.all, "draft-catalog", id, params] as const,
  detail: (feedId: string) => [...liveKeys.all, "detail", feedId] as const,
  capabilities: (pageId: string) =>
    [...liveKeys.all, "capabilities", pageId] as const,
};

export function useLiveListQuery(params?: {
  pageId?: string;
  q?: string;
  status?: "draft" | "live" | "ended" | "other";
}) {
  return useQuery({
    queryKey: liveKeys.list(params),
    queryFn: () => liveApi.list(params),
  });
}

export function useLiveDraftQuery(draftId: string | undefined) {
  return useQuery({
    queryKey: liveKeys.draft(draftId ?? ""),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    queryFn: () => liveApi.getDraft(draftId as string),
    enabled: Boolean(draftId),
  });
}

export function useLiveDraftCatalogQuery(
  draftId: string | undefined,
  params?: {
    q?: string;
    offset?: number;
    limit?: number;
    groupByProduct?: boolean;
  },
) {
  return useQuery({
    queryKey: liveKeys.draftCatalog(draftId ?? "", params),
    queryFn: () => liveApi.draftCatalog(draftId as string, params),
    enabled: Boolean(draftId),
  });
}

export function useLiveDraftCatalogInfiniteQuery(
  draftId: string | undefined,
  params?: {
    q?: string;
    limit?: number;
    groupByProduct?: boolean;
  },
) {
  const limit = params?.limit ?? CATALOG_PAGE_LIMIT;
  return useInfiniteQuery({
    queryKey: liveKeys.draftCatalog(draftId ?? "", {
      q: params?.q,
      limit,
      groupByProduct: params?.groupByProduct,
    }),
    queryFn: ({ pageParam }) =>
      liveApi.draftCatalog(draftId as string, {
        q: params?.q,
        offset: pageParam,
        limit,
        groupByProduct: params?.groupByProduct,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const pageLength = lastPage.products?.length ?? lastPage.items.length;
      if (pageLength === 0) return undefined;
      const loaded = pages.reduce(
        (sum, page) => sum + (page.products?.length ?? page.items.length),
        0,
      );
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: Boolean(draftId),
  });
}

export function useLiveDetailQuery(feedId: string | undefined) {
  return useQuery({
    queryKey: liveKeys.detail(feedId ?? ""),
    queryFn: () => liveApi.getDetail(feedId as string),
    enabled: Boolean(feedId),
  });
}

export function useLiveCapabilitiesQuery(pageId: string | undefined) {
  return useQuery({
    queryKey: liveKeys.capabilities(pageId ?? ""),
    queryFn: () => liveApi.capabilities(pageId as string),
    enabled: Boolean(pageId),
    staleTime: 60_000,
    retry: false,
  });
}

export function useCreateLiveDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: liveApi.createDraft,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: liveKeys.all });
    },
  });
}

export function usePatchLiveDraft(draftId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof liveApi.patchDraft>[1]) =>
      liveApi.patchDraft(draftId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: liveKeys.draft(draftId) });
      void qc.invalidateQueries({
        queryKey: [...liveKeys.all, "draft-catalog"],
      });
    },
  });
}

export function useAddDraftProduct(draftId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof liveApi.addDraftProduct>[1]) =>
      liveApi.addDraftProduct(draftId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: liveKeys.draft(draftId) });
      void qc.invalidateQueries({ queryKey: liveKeys.all });
    },
    onError: (e: Error) => {
      toast.error("ເພີ່ມສິນຄ້າບໍ່ສຳເລັດ", { description: e.message });
    },
  });
}

export function usePatchDraftProduct(draftId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      productId: string;
      body: Parameters<typeof liveApi.patchDraftProduct>[2];
    }) => liveApi.patchDraftProduct(draftId, input.productId, input.body),
    onSuccess: () => {
      return qc.invalidateQueries({ queryKey: liveKeys.draft(draftId) });
    },
    onError: (error: Error) =>
      toast.error(formatCfApiErrorMessage(error.message)),
  });
}

export function useRemoveDraftProduct(draftId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) =>
      liveApi.removeDraftProduct(draftId, productId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: liveKeys.draft(draftId) });
    },
  });
}

export function useCreateLiveFromDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { draftId: string; idempotencyKey: string }) =>
      liveApi.createLive(input.draftId, input.idempotencyKey),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: liveKeys.all });
    },
    onError: (e: Error) => {
      const code = e.message;
      if (code === "LIVE_CREATED_DB_INCOMPLETE") {
        toast.warning("ສ້າງ Live ສຳເລັດ ແຕ່ການບັນທຶກໃນລະບົບບໍ່ສົມບູນ");
        return;
      }
      toast.error("ສ້າງ Live ບໍ່ສຳເລັດ", {
        description: formatCfApiErrorMessage(e.message),
      });
    },
  });
}

export function useConnectExistingLive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { draftId: string; liveReference: string }) =>
      liveApi.connectExistingLive(input.draftId, input.liveReference),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: liveKeys.all });
      toast.success("ເຊື່ອມ Facebook Live ກັບລະບົບ CF ແລ້ວ");
    },
    onError: (error: Error) => {
      toast.error("ເຊື່ອມ Facebook Live ບໍ່ສຳເລັດ", {
        description: formatCfApiErrorMessage(error.message),
      });
    },
  });
}

export function useSyncLiveStatus(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => liveApi.syncStatus(feedId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: liveKeys.detail(feedId) });
      void qc.invalidateQueries({ queryKey: liveKeys.all });
    },
  });
}

export function useEndLive(feedId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => liveApi.endLive(feedId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: liveKeys.detail(feedId) });
      void qc.invalidateQueries({ queryKey: liveKeys.all });
      toast.success("ຈົບ Live ແລ້ວ");
    },
    onError: (e: Error) => {
      toast.error("ຈົບ Live ບໍ່ສຳເລັດ", { description: e.message });
    },
  });
}
